// web/src/player/censor.ts
import type { WordTiming, BleepRange } from "./types";
import { censorAllowance } from "./profanity";
import type { BleepMode } from "@/config/censorship";
import { DEFAULT_BLEEP_MODE } from "@/config/censorship";

export const CENSOR = { END_BUFFER: 0.05, MERGE_GAP: 0.1, DUCK_GAIN: 0.1 } as const;

export function buildBleeps(words: WordTiming[], bleepMode: BleepMode = DEFAULT_BLEEP_MODE): BleepRange[] {
  const out: BleepRange[] = [];
  for (const t of words) {
    const allow = censorAllowance(t.word, bleepMode);
    if (allow === null) continue;
    const start = t.start + allow / 1000;
    if (start >= t.end) continue;
    out.push({ start, end: t.end + CENSOR.END_BUFFER, word: t.word });
  }
  out.sort((a, b) => a.start - b.start);
  const merged: BleepRange[] = [];
  for (const b of out) {
    const last = merged[merged.length - 1];
    if (last && b.start <= last.end + CENSOR.MERGE_GAP) last.end = Math.max(last.end, b.end);
    else merged.push({ ...b });
  }
  return merged;
}

export const isCensoredAt = (time: number, bleeps: BleepRange[]) =>
  bleeps.some((b) => time >= b.start && time <= b.end);

/** Live bleep: loop 1khz.mp3 through WebAudio while in a bleep range. */
export class Bleeper {
  private node: AudioBufferSourceNode | null = null;
  private buffer: AudioBuffer | null = null;
  private active = false;
  constructor(private ctx: AudioContext, private out: AudioNode) {}
  async load(url = "/audio/1khz.mp3") {
    const ab = await (await fetch(url)).arrayBuffer();
    this.buffer = await this.ctx.decodeAudioData(ab);
  }
  start() {
    if (this.active || !this.buffer) return;
    this.node = this.ctx.createBufferSource();
    this.node.buffer = this.buffer; this.node.loop = true; this.node.connect(this.out); this.node.start();
    this.active = true;
  }
  stop() { if (this.node) { this.node.stop(); this.node.disconnect(); this.node = null; } this.active = false; }
  get isActive() { return this.active; }
}
